import { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw, Search, Save, Undo2, Wand2, Loader2, Download, Upload } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { adminApi, API } from "../../lib/api";
import { toast } from "sonner";

/**
 * Admin › Marcas comerciales.
 *
 * Muestra todos los productos con su proveedor (razón social de Arcos), la
 * marca inferida automáticamente del nombre y — si existe — el override
 * manual que hayamos guardado. Permite:
 *  - Corregir la marca a mano por producto (persistente).
 *  - Volver a la inferencia (borrar override).
 *  - Re-correr la inferencia sobre todo el catálogo (respeta overrides).
 *  - Filtrar por proveedor, marca, código o solo los editados a mano.
 */
export default function AdminMarcas() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [onlyManual, setOnlyManual] = useState(false);
  const [soloDifieren, setSoloDifieren] = useState(false);
  const [contraste, setContraste] = useState(null);
  const [applying, setApplying] = useState(false);
  const [skip, setSkip] = useState(0);
  const [edits, setEdits] = useState({}); // { producto_id: valorNuevo }
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState({ marcas: [], proveedores: [] });
  const fileInputRef = useRef(null);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/marcas", {
      params: {
        q: q || undefined,
        proveedor: proveedor || undefined,
        only_manual: onlyManual || undefined,
        solo_difieren: soloDifieren || undefined,
        limit, skip,
      },
    })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("No se pudieron cargar los productos"))
      .finally(() => setLoading(false));
  }, [q, proveedor, onlyManual, soloDifieren, skip]);

  const loadContraste = useCallback(() => {
    adminApi.get("/admin/marcas/contraste").then((r) => setContraste(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadContraste(); }, [loadContraste]);
  useEffect(() => {
    adminApi.get("/admin/marcas/summary").then((r) => setSummary(r.data || {})).catch(() => {});
  }, []);

  const saveOne = async (pid) => {
    const newMarca = (edits[pid] ?? "").trim();
    try {
      await adminApi.post(`/admin/products/${encodeURIComponent(pid)}/marca`, { marca: newMarca || null });
      toast.success(newMarca ? `Marca guardada: ${newMarca}` : "Volviendo a la marca de Arcos");
      setEdits((prev) => { const c = { ...prev }; delete c[pid]; return c; });
      load(); loadContraste();
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const clearOverride = async (pid) => {
    try {
      await adminApi.post(`/admin/products/${encodeURIComponent(pid)}/marca`, { marca: null });
      toast.success("Corrección eliminada — vuelve la marca de Arcos");
      setEdits((prev) => { const c = { ...prev }; delete c[pid]; return c; });
      load(); loadContraste();
    } catch { toast.error("No se pudo eliminar la corrección"); }
  };

  const usarArcos = async (it) => {
    if (!it.marca_arcos) { toast.error("Arcos no tiene marca para este producto"); return; }
    try {
      await adminApi.post(`/admin/products/${encodeURIComponent(it.producto_id)}/marca`, { marca: null });
      toast.success(`Usando la marca de Arcos: ${it.marca_arcos}`);
      setEdits((prev) => { const c = { ...prev }; delete c[it.producto_id]; return c; });
      load(); loadContraste();
    } catch { toast.error("No se pudo aplicar la marca de Arcos"); }
  };

  const aplicarArcosMasivo = async () => {
    setApplying(true);
    try {
      const r = await adminApi.post("/admin/marcas/aplicar-arcos", null, {
        params: { solo_difieren: true },
      });
      toast.success(
        `${r.data.aplicados} productos ahora usan la marca de Arcos · ` +
        `${r.data.overrides_respetados} correcciones manuales respetadas`
      );
      load(); loadContraste();
    } catch { toast.error("Error aplicando las marcas de Arcos"); }
    finally { setApplying(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await adminApi.get("/admin/marcas/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "marcas_comerciales_plantilla.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Plantilla descargada");
    } catch {
      toast.error("No se pudo descargar la plantilla");
    }
  };

  const uploadExcel = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await adminApi.post("/admin/marcas/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data;
      let msg = `${d.aplicados} marcas aplicadas`;
      if (d.borrados) msg += `, ${d.borrados} borrados (vuelven a inferencia)`;
      if (d.no_encontrados_total) msg += `, ⚠ ${d.no_encontrados_total} códigos no existen en Arcos`;
      if (d.errores?.length) msg += `, ${d.errores.length} filas con error`;
      toast.success(msg);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error subiendo el Excel");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="space-y-6" data-testid="admin-marcas-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Comercial</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Marcas comerciales</h1>
          <p className="text-sm text-cm-ink-soft mt-1 max-w-3xl">
            La marca la manda <strong>Arcos</strong> (campo <em>Marca</em> del ERP: QUALA, POSTOBON,
            AJOVER DARNEL…). Ya no se adivina del nombre del producto. Aquí contrastas la marca de
            Arcos con la del portal y con la que la vieja heurística habría inferido, y puedes
            corregir una a mano — esa corrección sobrevive a las sincronizaciones.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline" className="border-cm-ink hover:bg-cm-ink hover:text-cm-cream"
            onClick={downloadTemplate}
            data-testid="marca-template-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Plantilla Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm"
            className="hidden"
            onChange={(e) => uploadExcel(e.target.files?.[0])}
            data-testid="marca-upload-input"
          />
          <Button
            variant="outline" className="border-cm-yellow bg-cm-yellow/10 text-cm-ink hover:bg-cm-yellow/30"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="marca-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Subir Excel
          </Button>
          <Button
            onClick={aplicarArcosMasivo}
            disabled={applying}
            className="btn-cm-primary"
            data-testid="aplicar-arcos-btn"
            title="Pone la marca de Arcos donde el portal difiere (respeta las correcciones manuales)"
          >
            {applying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Aplicar marca de Arcos
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="marcas-kpis">
        <Card className="p-4 bg-white border-cm-border">
          <div className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Productos</div>
          <div className="font-serif text-2xl mt-1">{(contraste?.total_productos ?? total).toLocaleString("es-CO")}</div>
        </Card>
        <Card className="p-4 bg-white border-cm-border">
          <div className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Marcas en Arcos</div>
          <div className="font-serif text-2xl mt-1">{contraste?.marcas_distintas_arcos ?? "—"}</div>
        </Card>
        <Card className="p-4 bg-white border-cm-border">
          <div className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Marcas en el portal</div>
          <div className="font-serif text-2xl mt-1">{contraste?.marcas_distintas_portal ?? "—"}</div>
        </Card>
        <Card className={`p-4 ${contraste?.difieren ? "bg-cm-red/5 border-cm-red/40" : "bg-white border-cm-border"}`}>
          <div className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Difieren de Arcos</div>
          <div className="font-serif text-2xl mt-1" data-testid="kpi-difieren">{contraste?.difieren ?? "—"}</div>
        </Card>
        <Card className="p-4 bg-cm-yellow/10 border-cm-yellow">
          <div className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Correcciones manuales</div>
          <div className="font-serif text-2xl mt-1">{contraste?.overrides_manuales ?? 0}</div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-4 bg-white border-cm-border">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cm-ink-soft" />
            <Input
              placeholder="Buscar por nombre, código o proveedor…"
              className="pl-9 h-10 border-cm-border"
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              data-testid="marca-search"
            />
          </div>
          <select
            value={proveedor}
            onChange={(e) => { setProveedor(e.target.value); setSkip(0); }}
            className="h-10 border border-cm-border rounded px-3 text-sm bg-white min-w-[220px]"
            data-testid="marca-filter-proveedor"
          >
            <option value="">Todas las marcas de Arcos</option>
            {(summary.proveedores || []).map((p) => (
              <option key={p.proveedor} value={p.proveedor}>{p.proveedor} ({p.count})</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-cm-ink cursor-pointer">
            <Switch checked={soloDifieren} onCheckedChange={(v) => { setSoloDifieren(v); setSkip(0); }} data-testid="marca-only-difieren" />
            Solo donde difiere de Arcos
          </label>
          <label className="flex items-center gap-2 text-sm text-cm-ink cursor-pointer">
            <Switch checked={onlyManual} onCheckedChange={(v) => { setOnlyManual(v); setSkip(0); }} data-testid="marca-only-manual" />
            Solo corregidos a mano
          </label>
          <Button
            variant="outline" size="sm"
            onClick={load}
            className="border-cm-ink hover:bg-cm-ink hover:text-cm-cream"
            data-testid="marca-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>
      </Card>

      {/* Tabla */}
      <Card className="bg-white border-cm-border shadow-none overflow-hidden">
        {loading ? (
          <p className="p-8 text-cm-ink-soft text-center">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-cm-ink-soft text-center">Sin resultados con estos filtros.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="marcas-table">
              <thead className="text-left text-xs uppercase tracking-wider text-cm-ink-soft bg-cm-cream-alt/40">
                <tr>
                  <th className="px-4 py-3">Producto</th>
                  <th className="px-4 py-3">Marca en Arcos</th>
                  <th className="px-4 py-3">Inferida (vieja)</th>
                  <th className="px-4 py-3">Marca del portal</th>
                  <th className="px-4 py-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {items.map((it) => {
                  const currentEdit = edits[it.producto_id];
                  const displayed = currentEdit !== undefined ? currentEdit : (it.marca_final || "");
                  const dirty = currentEdit !== undefined && currentEdit !== (it.marca_final || "");
                  return (
                    <tr key={it.producto_id} className="hover:bg-cm-cream-alt/30" data-testid={`marca-row-${it.producto_id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-cm-ink leading-tight" title={it.nombre}>
                          <span className="line-clamp-2">{it.nombre}</span>
                        </div>
                        <div className="text-[11px] font-mono text-cm-ink-soft mt-0.5">#{it.producto_id} · {it.sublinea || it.categoria}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge className="bg-cm-ink text-cm-cream hover:bg-cm-ink text-xs font-mono max-w-[190px] truncate inline-block" title={it.marca_arcos}>
                          {it.marca_arcos || "—"}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs text-cm-ink-soft font-mono line-through opacity-60">
                          {it.marca_inferida || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <Input
                            value={displayed}
                            onChange={(e) => setEdits((prev) => ({ ...prev, [it.producto_id]: e.target.value.toUpperCase() }))}
                            className="h-8 w-44 border-cm-border font-mono text-xs"
                            data-testid={`marca-input-${it.producto_id}`}
                          />
                          {it.marca_override && !dirty && (
                            <Badge className="bg-cm-yellow text-cm-ink hover:bg-cm-yellow text-[10px]">MANUAL</Badge>
                          )}
                          {it.difiere && !it.marca_override && !dirty && (
                            <Badge variant="outline" className="border-cm-red/50 text-cm-red text-[10px]" data-testid={`marca-difiere-${it.producto_id}`}>≠ ARCOS</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex gap-1">
                          {dirty && (
                            <Button
                              size="sm" className="btn-cm-primary h-8"
                              onClick={() => saveOne(it.producto_id)}
                              data-testid={`marca-save-${it.producto_id}`}
                            >
                              <Save className="h-3.5 w-3.5 mr-1" /> Guardar
                            </Button>
                          )}
                          {!dirty && it.difiere && (
                            <Button
                              size="sm" variant="outline" className="h-8 border-cm-ink text-xs hover:bg-cm-ink hover:text-cm-cream"
                              onClick={() => usarArcos(it)}
                              data-testid={`marca-usar-arcos-${it.producto_id}`}
                              title={`Usar la marca de Arcos: ${it.marca_arcos}`}
                            >
                              Usar Arcos
                            </Button>
                          )}
                          {it.marca_override && !dirty && (
                            <Button
                              size="sm" variant="outline" className="h-8 border-cm-border"
                              onClick={() => clearOverride(it.producto_id)}
                              data-testid={`marca-clear-${it.producto_id}`}
                              title="Quitar la corrección manual y volver a la marca de Arcos"
                            >
                              <Undo2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paginación */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm text-cm-ink-soft">
          <span>Página {currentPage} de {pageCount}. Total {total.toLocaleString("es-CO")} productos.</span>
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline" className="border-cm-border"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              data-testid="marca-prev-page"
            >Anterior</Button>
            <Button
              size="sm" variant="outline" className="border-cm-border"
              disabled={skip + limit >= total}
              onClick={() => setSkip(skip + limit)}
              data-testid="marca-next-page"
            >Siguiente</Button>
          </div>
        </div>
      )}
    </div>
  );
}
